import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Keyboard, Command } from 'lucide-react';

/**
 * D2. Keyboard shortcut cheat sheet.
 *
 * Opens on `?` press (unless typing) and lists every shortcut in
 * LodgiFlow, grouped by area. Mount once, high in the tree.
 *
 *   <KeyboardShortcutsHelp />
 *
 * You can also open it programmatically:
 *   window.dispatchEvent(new CustomEvent('lodgiflow:show-shortcuts'))
 */

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

const GROUPS = [
  {
    id: 'nav',
    label: 'Navigation',
    items: [
      { keys: [MOD, 'K'], desc: 'Open command palette · jump to anything' },
      { keys: ['?'], desc: 'Show this keyboard shortcuts sheet' },
      { keys: ['G', 'H'], desc: 'Go to Home dashboard' },
      { keys: ['G', 'V'], desc: 'Go to Stack Map' },
      { keys: ['G', 'D'], desc: 'Go to Tech Database' },
      { keys: ['G', 'R'], desc: 'Go to Reports' },
      { keys: ['G', 'A'], desc: 'Go to Admin console' },
    ],
  },
  {
    id: 'stackmap',
    label: 'Stack Map',
    items: [
      { keys: ['F'], desc: 'Fit graph to view' },
      { keys: ['R'], desc: 'Reset zoom' },
      { keys: ['L'], desc: 'Cycle layout' },
      { keys: ['S'], desc: 'Toggle sidebar' },
      { keys: ['1', '–', '0'], desc: 'Toggle category visibility' },
      { keys: ['/'], desc: 'Focus search' },
      { keys: ['Shift', 'F'], desc: 'Focus mode — hide chrome' },
      { keys: ['Esc'], desc: 'Exit focus / close dialogs' },
    ],
  },
  {
    id: 'edit',
    label: 'Editing',
    items: [
      { keys: [MOD, 'S'], desc: 'Save current form / page' },
      { keys: [MOD, 'Z'], desc: 'Undo last action (where supported)' },
      { keys: [MOD, 'Enter'], desc: 'Submit / confirm dialog' },
      { keys: ['Esc'], desc: 'Cancel / close dropdown / popover' },
    ],
  },
];

function KeyChip({ children }) {
  return (
    <kbd className="inline-flex min-w-[24px] h-6 items-center justify-center rounded-md border border-border bg-muted px-1.5 text-[11px] font-mono font-semibold text-foreground shadow-sm">
      {children}
    </kbd>
  );
}

export default function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== '?' && !(e.key === '/' && e.shiftKey)) return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      setOpen(v => !v);
    };
    const evtHandler = () => setOpen(true);
    document.addEventListener('keydown', handler);
    window.addEventListener('lodgiflow:show-shortcuts', evtHandler);
    return () => {
      document.removeEventListener('keydown', handler);
      window.removeEventListener('lodgiflow:show-shortcuts', evtHandler);
    };
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="keyboard-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-violet-500" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Press <KeyChip>?</KeyChip> anywhere to open this sheet. LodgiFlow is fastest on the keyboard.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 py-2">
          {GROUPS.map(g => (
            <div key={g.id}>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-violet-600 mb-2 flex items-center gap-1.5">
                {g.id === 'nav' && <Command className="h-3 w-3" />}
                {g.label}
              </div>
              <div className="space-y-1.5">
                {g.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 py-1 text-xs">
                    <span className="text-foreground/80 flex-1">{it.desc}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {it.keys.map((k, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && k !== '–' && <span className="text-muted-foreground text-[10px]">+</span>}
                          {k === '–' ? <span className="text-muted-foreground text-[10px]">to</span> : <KeyChip>{k}</KeyChip>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
